import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Clock3,
  FileText,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";
import { useAdminPortal } from "../AdminPortalContext";

const AdminDashboardPage = () => {
  const { adminProfile, formatDate, submissions, summary, theme } =
    useAdminPortal();
  const isDarkTheme = theme === "dark";
  const recentSubmissions = submissions.slice(0, 5);

  const cards = [
    {
      label: "Total Submissions",
      value: summary.totalSubmissions,
      icon: Users,
      accent: "from-sky-500 to-cyan-500",
    },
    {
      label: "Today",
      value: summary.todayCount,
      icon: Clock3,
      accent: "from-amber-500 to-orange-500",
    },
    {
      label: "Last 7 Days",
      value: summary.lastSevenDaysCount,
      icon: TrendingUp,
      accent: "from-emerald-500 to-teal-500",
    },
    {
      label: "Top Interest",
      value: summary.topInterest,
      icon: Sparkles,
      accent: "from-violet-500 to-fuchsia-500",
    },
  ];

  return (
    <div className="space-y-6">
      <section
        className={`rounded-[28px] border px-6 py-6 sm:px-8 sm:py-8 ${isDarkTheme ? "border-slate-800 bg-[linear-gradient(135deg,#0f172a_0%,#111827_100%)]" : "border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fbff_65%,#fff7ec_100%)] shadow-[0_20px_60px_rgba(15,23,42,0.06)]"}`}
      >
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-600">
              Welcome back
            </p>
            <h2 className="mt-3 text-3xl font-bold leading-tight">
              {adminProfile?.email || "Admin"}
            </h2>
            <p
              className={`mt-3 max-w-2xl text-sm leading-7 ${isDarkTheme ? "text-slate-400" : "text-slate-600"}`}
            >
              This portal is now structured for future growth. Today it shows
              lead activity and exports; later you can plug in analytics,
              workflows, user management, and operational controls without
              redesigning the shell.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link
              to="../submissions"
              className={`inline-flex items-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold transition ${isDarkTheme ? "bg-sky-500 text-slate-950 hover:bg-sky-400" : "bg-slate-900 text-white hover:bg-slate-800"}`}
            >
              Review Submissions
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="../exports"
              className={`inline-flex items-center gap-2 rounded-2xl border px-5 py-3 text-sm font-semibold transition ${isDarkTheme ? "border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
            >
              Open Export Center
            </Link>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => {
          const Icon = card.icon;

          return (
            <article
              key={card.label}
              className={`rounded-[24px] border p-5 ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
            >
              <div
                className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${card.accent} text-white shadow-lg`}
              >
                <Icon className="h-5 w-5" />
              </div>
              <p
                className={`mt-5 text-sm font-semibold ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
              >
                {card.label}
              </p>
              <p className="mt-2 text-2xl font-bold break-words">
                {card.value}
              </p>
            </article>
          );
        })}
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-[1.4fr_0.9fr]">
        <article
          className={`rounded-[28px] border ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
        >
          <div
            className={`flex items-center justify-between border-b px-6 py-5 ${isDarkTheme ? "border-slate-800" : "border-slate-200"}`}
          >
            <div>
              <h3 className="text-lg font-bold">Recent Leads</h3>
              <p
                className={`mt-1 text-sm ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
              >
                Latest contact form activity
              </p>
            </div>
            <Link
              to="../submissions"
              className="text-sm font-semibold text-sky-600 hover:text-sky-500"
            >
              View all
            </Link>
          </div>

          <div className="divide-y divide-slate-200 dark:divide-slate-800">
            {recentSubmissions.length === 0 && (
              <div
                className={`px-6 py-10 text-sm ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
              >
                No submissions yet. Once leads start coming in, this area
                becomes the quick review queue.
              </div>
            )}

            {recentSubmissions.map((submission) => (
              <div
                key={submission.id}
                className="flex flex-col gap-3 px-6 py-5 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-bold">{submission.name}</p>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${isDarkTheme ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-600"}`}
                    >
                      {submission.interest}
                    </span>
                  </div>
                  <p
                    className={`mt-1 text-sm ${isDarkTheme ? "text-slate-400" : "text-slate-600"}`}
                  >
                    {submission.email}
                  </p>
                  <p
                    className={`mt-2 line-clamp-2 text-sm leading-6 ${isDarkTheme ? "text-slate-300" : "text-slate-600"}`}
                  >
                    {submission.message}
                  </p>
                </div>
                <p
                  className={`whitespace-nowrap text-xs font-medium ${isDarkTheme ? "text-slate-500" : "text-slate-400"}`}
                >
                  {formatDate(submission.createdAt)}
                </p>
              </div>
            ))}
          </div>
        </article>

        <div className="space-y-6">
          <article
            className={`rounded-[28px] border p-6 ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
          >
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-100 text-sky-700">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold">Portal Status</h3>
                <p
                  className={`text-sm ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
                >
                  UI scaffold ready for later integration
                </p>
              </div>
            </div>

            <ul
              className={`mt-5 space-y-3 text-sm ${isDarkTheme ? "text-slate-300" : "text-slate-600"}`}
            >
              <li>Structured sidebar and nested route layout</li>
              <li>Live submissions page connected to backend</li>
              <li>Dedicated export center with CSV and PDF actions</li>
              <li>Theme settings persisted for admins</li>
            </ul>
          </article>

          <article
            className={`rounded-[28px] border p-6 ${isDarkTheme ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white shadow-[0_16px_44px_rgba(15,23,42,0.05)]"}`}
          >
            <h3 className="text-lg font-bold">Next Integrations</h3>
            <p
              className={`mt-2 text-sm leading-6 ${isDarkTheme ? "text-slate-400" : "text-slate-500"}`}
            >
              This structure is ready for analytics widgets, lead assignment,
              notes, tags, automation rules, and role-based access later.
            </p>
          </article>
        </div>
      </section>
    </div>
  );
};

export default AdminDashboardPage;
